/**
 * Shared pieces of the storage modals (category drill-down + duplicates
 * review) — extracted when the duplicates modal arrived so the two can't
 * drift (followups §2.1).
 *
 * `InlineRetentionField` and `parseField` moved here from
 * `VersionRetentionPage` when the By-folder tab needed the same pencil-edit
 * shape. Two copies of a field whose whole job is "the value on screen is the
 * value that will be saved" is exactly the drift this file exists to prevent.
 */
import type { ReactNode } from 'react'
import { PDAppIcon, Tooltip } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { CAP_ROLES } from '@papan/types'

/**
 * Themed checkbox — a real `<input type="checkbox">` (keyboard, labels and
 * testing-library all keep working) with the native paint replaced:
 * appearance-none box, blue fill with a drawn check/dash when active, and
 * the check driven by props rather than `peer-indeterminate:` CSS so it
 * needs nothing from the Tailwind version.
 */
export function Checkbox({
  checked,
  indeterminate = false,
  disabled = false,
  onChange,
  title,
  'aria-label': ariaLabel,
}: {
  checked: boolean
  indeterminate?: boolean
  /** Read-only row: the caller can SEE the file but not delete it, so the
   *  control must not pretend otherwise (followups §3.3). */
  disabled?: boolean
  onChange: () => void
  title?: string
  'aria-label'?: string
}) {
  const active = checked || indeterminate
  return (
    <span className="relative inline-flex w-4 h-4 shrink-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        title={title}
        aria-label={ariaLabel}
        ref={(el) => { if (el) el.indeterminate = indeterminate }}
        className={`appearance-none w-4 h-4 rounded border-[1.5px] transition-all duration-150
          ${disabled ? 'cursor-not-allowed opacity-40 themed-border' : 'cursor-pointer'}
          ${active ? 'bg-blue-500 border-blue-500' : disabled ? 'bg-transparent' : 'bg-transparent themed-border hover:border-blue-400'}
          focus-visible:ring-2 focus-visible:ring-blue-500/50`}
      />
      {active && (
        <svg
          viewBox="0 0 16 16"
          className="pointer-events-none absolute inset-0 m-auto w-3 h-3 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {indeterminate ? <path d="M3.5 8h9" /> : <path d="M3 8.5l3.5 3.5L13 5" />}
        </svg>
      )}
    </span>
  )
}

/**
 * Middle-ellipsis a path, always keeping the LAST segment — CSS `truncate`
 * cuts the end, which is exactly the part that tells two deep folders
 * apart. Keeps as many leading segments as fit; put the full path in the
 * title tooltip.
 */
/**
 * A ROLE as a human reads it, never the stored id.
 *
 * The chat-storage screens printed `membership.role` raw, so the
 * Defaults-by-role table had a row labelled "Noaccess" — a word no other
 * screen in this product shows anyone — and labelled `admin` as "Admin"
 * where the product's own name for it is "Drive admin". The same raw id
 * was interpolated into two sentences on Adjust limit, where in the three
 * non-English locales it stayed English inside a translated sentence
 * (full review 43C, B9).
 *
 * `CAP_ROLES` is the canonical label set and is what the AI-budget's
 * role-caps table renders — the screen this dialog is explicitly modelled
 * on, so the two now agree. `noaccess` is deliberately outside `CAP_ROLES`
 * (it can never run anything) but IS a legitimate stored role, so it gets
 * its label here.
 *
 * The labels are English by construction, exactly as on the budget screen.
 * Translating role names is a product-wide change — doing it on this one
 * screen would just move the inconsistency — so it is not done here.
 */
export function roleLabel(role: string): string {
  if (role === 'noaccess') return 'No access'
  const known = CAP_ROLES.find((r) => r.id === role)
  if (known) return known.label
  // An id the code has never heard of. Shown as stored rather than
  // prettified, because "someone configured something we do not know
  // about" is the useful signal — the same distinction `CustomerPeople`
  // draws with its `knownRole`.
  return role
}

export function shortenPath(path: string, max = 44): string {
  if (path.length <= max) return path
  const segs = path.replace(/^\//, '').split('/')
  const last = segs.pop()!
  let head = ''
  for (const s of segs) {
    const next = head ? `${head}/${s}` : `/${s}`
    if (`${next}/…/${last}`.length > max) break
    head = next
  }
  return head ? `${head}/…/${last}` : `…/${last}`
}

/** Compact modified-date: same year → "Jul 17", older → "Jul 2025". */
export function fmtDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString(undefined, sameYear ? { month: 'short', day: 'numeric' } : { year: 'numeric', month: 'short' })
}

/** Display label for a folder path — VFS root stripped: 'papan/ttt' → '/ttt'. */
export function folderLabel(folder: string | null): string {
  return (folder ?? 'papan').replace(/^papan(\/|$)/, '/').replace(/^\/\//, '/') || '/'
}

/**
 * Two inline glyphs for the reclaimable row actions.
 *
 * Hand-drawn rather than imported: `@papan/orgs` does not depend on
 * lucide-react (only `@papan/agent` does), and pulling a whole icon
 * package in for two paths is the wrong trade — this file already draws
 * the Checkbox's own check/dash the same way. Stroke geometry follows
 * lucide's 24-grid so they sit consistently beside the app's other icons.
 */
function Glyph({ size = 15, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth={1.75}
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false"
    >
      {children}
    </svg>
  )
}

/** Permanent delete — the reclaimable rows' destructive action. */
export function TrashGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </Glyph>
  )
}

/** Navigate away to the full surface (Bin / Quarantine). */
export function OpenGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M7 17 17 7" />
      <path d="M7 7h10v10" />
    </Glyph>
  )
}

/** The over-allocation warning's triangle (#758). Here with the other
 *  glyphs rather than from `lucide-react`: that package is a dependency of
 *  `apps/web`, not of this feature, so importing it inside `features/orgs`
 *  would reach across a package boundary for one icon. */
export function WarnGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M12 3l9.5 16.5h-19z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </Glyph>
  )
}

/** Manage version-retention settings — the "old versions" row's config action
 *  (#590). Sliders geometry (lucide `sliders-horizontal`): "adjust how much is
 *  kept", distinct from the red Trash (delete now) beside it. */
export function RetentionGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M21 4h-7" />
      <path d="M10 4H3" />
      <path d="M21 12h-9" />
      <path d="M8 12H3" />
      <path d="M21 20h-5" />
      <path d="M12 20H3" />
      <path d="M14 2v4" />
      <path d="M8 10v4" />
      <path d="M16 18v4" />
    </Glyph>
  )
}

/** A plain folder, for the "By folder" tab's rule rows — the row's subject is
 *  a folder, so the leading mark says so. Lucide `folder` geometry.
 *
 *  It does NOT vary with the rule's state. A struck-through variant was tried
 *  and removed: the leading mark is an identity, and making it also carry the
 *  state put a third voice on a row that already says what it does in its
 *  value box and in a line of plain words beneath. */
export function FolderShapeGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </Glyph>
  )
}

/** "What does this mean?" — opens the explainer dialog beside a control whose
 *  states are not self-evident. Lucide `info` geometry. */
export function InfoGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </Glyph>
  )
}

/** Edit affordance for the "By file type" accordion's per-field inline edit
 *  (#590 follow-up, collapsible + pencil-edit redesign). Lucide `pencil`
 *  geometry. */
export function PencilGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
      <path d="m15 5 4 4" />
    </Glyph>
  )
}

/** Confirm/save an inline-edit field. Lucide `check` geometry. */
export function CheckGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M20 6 9 17l-5-5" />
    </Glyph>
  )
}

/** Cancel an inline-edit field without saving. Lucide `x` geometry. */
export function CrossGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </Glyph>
  )
}

/** Reset an inline-edit field to its inherited default. Lucide `rotate-ccw`
 *  geometry — distinct from Cancel: this one WRITES (clears the override),
 *  Cancel does not. */
export function ResetGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M3 12a9 9 0 1 0 2.636-6.364" />
      <path d="M3 4v5h5" />
    </Glyph>
  )
}

/** The "By file type" accordion's expand/collapse indicator (#590 polish).
 *  Rotated 90° via `transform` (never re-drawn) so the caller can animate it
 *  cheaply — see `VersionRetentionPage`. Lucide `chevron-right` geometry. */
export function ChevronRightGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="m9 18 6-6-6-6" />
    </Glyph>
  )
}

/** Drag handle — Lucide `grip-vertical` geometry. The grab affordance on a
 *  reorderable row; the same mark the launcher's shortcut list uses. */
export function GripGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="5" r="1" />
      <circle cx="9" cy="19" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="5" r="1" />
      <circle cx="15" cy="19" r="1" />
    </Glyph>
  )
}

/** Lucide `chevron-up`. The keyboard half of a drag-reorder list. */
export function ChevronUpGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="m18 15-6-6-6 6" />
    </Glyph>
  )
}

/** Lucide `chevron-down`. */
export function ChevronDownGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="m6 9 6 6 6-6" />
    </Glyph>
  )
}

/** Kind icon — image files. Lucide `image` geometry. */
export function ImageGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </Glyph>
  )
}

/** Kind icon — video files. Lucide `film` geometry. */
export function VideoGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M7 3v18" />
      <path d="M3 7.5h4" />
      <path d="M3 12h18" />
      <path d="M3 16.5h4" />
      <path d="M17 3v18" />
      <path d="M17 7.5h4" />
      <path d="M17 16.5h4" />
    </Glyph>
  )
}

/** Kind icon — audio files. Lucide `music` geometry. */
export function AudioGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </Glyph>
  )
}

/** Kind icon — office documents. Lucide `file-text` geometry. */
export function DocGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M10 9H8" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </Glyph>
  )
}

/** Kind icon — PDF. A plain file glyph (no text lines) so it reads distinct
 *  from `DocGlyph` at a glance. Lucide `file` geometry. */
export function PdfGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    </Glyph>
  )
}

/** Kind icon — text and code. Lucide `code` geometry. */
export function CodeGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <path d="m16 18 6-6-6-6" />
      <path d="m8 6-6 6 6 6" />
    </Glyph>
  )
}

/** Kind icon — PDApps. Lucide `layout-grid` geometry. */
export function AppGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size}>
      <rect width="7" height="7" x="3" y="3" rx="1" />
      <rect width="7" height="7" x="14" y="3" rx="1" />
      <rect width="7" height="7" x="14" y="14" rx="1" />
      <rect width="7" height="7" x="3" y="14" rx="1" />
    </Glyph>
  )
}

/** Maps a curated retention kind (`RETENTION_KINDS` in `VersionRetentionPage`)
 *  to its accordion-header icon. Unknown kinds fall back to `DocGlyph` — same
 *  "generic file" reading a real drive already gives an unstyled extension. */
export function kindGlyph(kind: string, size?: number): ReactNode {
  switch (kind) {
    case 'image': return <ImageGlyph size={size} />
    case 'video': return <VideoGlyph size={size} />
    case 'audio': return <AudioGlyph size={size} />
    case 'office': return <DocGlyph size={size} />
    case 'text': return <CodeGlyph size={size} />
    case 'pdapp': return <PDAppIcon size={size ?? 15} />
    default: return <DocGlyph size={size} />
  }
}

export function parseField(text: string): { valid: boolean; value: number | null } {
  const trimmed = text.trim()
  if (trimmed === '') return { valid: true, value: null }
  const n = Number(trimmed)
  if (Number.isInteger(n) && n >= 1 && n <= 500) return { valid: true, value: n }
  return { valid: false, value: null }
}

/**
 * One retention value: read-only box + pencil, or (once clicked) an
 * editable input + confirm/cancel/reset. The ONLY place this shape is
 * implemented — the tenant maximum, every kind and every extension row
 * (present or stranded under "Other overrides") all render through this.
 *
 * Only one field on the page is ever `editing` at a time — the page tracks a
 * single `editingField` id and passes `editing={editingField === id}` to
 * every instance, so clicking a second field's pencil silently abandons the
 * first (`onEdit` just reassigns which id is current; nothing needs to be
 * confirmed or discarded first).
 */
export function InlineRetentionField({
  id,
  value,
  inheritedPlaceholder,
  editing,
  saving,
  error,
  editValue,
  onEditValueChange,
  onEdit,
  onCancel,
  onConfirm,
  onReset,
  label,
  className,
}: {
  id: string
  /** The tenant's own explicit override, or `null` when this field inherits. */
  value: number | null
  /** What this field resolves to today when it has no explicit override —
   *  shown as the read-only display and as the input's placeholder. */
  inheritedPlaceholder: number
  editing: boolean
  saving: boolean
  error?: string | null
  editValue: string
  onEditValueChange: (v: string) => void
  onEdit: () => void
  onCancel: () => void
  onConfirm: () => void
  onReset: () => void
  /** Accessible name for the input (and the basis for the pencil's). */
  label: string
  className?: string
}) {
  const { t } = useTranslation('core-ui')

  const parsed = parseField(editValue)
  // ONE fixed-footprint wrapper for BOTH states, left-aligned, so entering
  // edit mode never shifts the value/input horizontally (the read-only pencil
  // and the edit tick sit at the same x; cross+reset just fill the reserved
  // width to their right) and never changes the row height (the error is
  // absolutely positioned). `w-[152px]` = input(w-16=64) + 3 icons(3×24) +
  // gaps — the widest state. Without this, read-only was `[value+pencil]`
  // ~92px and edit `flex-col items-end [input+3 icons]` ~148px, so the field
  // jumped right and down on click. `justify-start` keeps the first child (the
  // value in read-only, the input in edit) pinned to the same left edge.
  return (
    <div className={`relative flex items-center gap-1 w-[152px] shrink-0 ${className ?? ''}`}>
      {editing ? (
        <>
          <input
            autoFocus
            data-testid={`vr-input-${id}`}
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            disabled={saving}
            inputMode="numeric"
            placeholder={String(inheritedPlaceholder)}
            aria-label={label}
            className={`themed-input border rounded px-2 py-1 text-xs w-16 outline-none focus:border-blue-500 disabled:opacity-50 ${
              !parsed.valid ? 'border-red-500' : ''
            }`}
          />
          <Tooltip content={t('settings.versionRetention.field.confirm')} always placement="top" className="inline-flex shrink-0">
            <button
              type="button"
              data-testid={`vr-confirm-${id}`}
              onClick={onConfirm}
              disabled={saving || !parsed.valid}
              aria-label={t('settings.versionRetention.field.confirm')}
              className="text-green-500 hover:text-green-400 disabled:opacity-40 shrink-0 w-6 h-6 flex items-center justify-center outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500/50"
            >
              <CheckGlyph size={13} />
            </button>
          </Tooltip>
          <Tooltip content={t('settings.versionRetention.field.cancel')} always placement="top" className="inline-flex shrink-0">
            <button
              type="button"
              data-testid={`vr-cancel-${id}`}
              onClick={onCancel}
              disabled={saving}
              aria-label={t('settings.versionRetention.field.cancel')}
              className="themed-text-muted hover:themed-text disabled:opacity-40 shrink-0 w-6 h-6 flex items-center justify-center outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500/50"
            >
              <CrossGlyph size={13} />
            </button>
          </Tooltip>
          <Tooltip content={t('settings.versionRetention.field.reset')} always placement="top" className="inline-flex shrink-0">
            <button
              type="button"
              data-testid={`vr-reset-${id}`}
              onClick={onReset}
              disabled={saving}
              aria-label={t('settings.versionRetention.field.reset')}
              className="themed-text-muted hover:themed-text disabled:opacity-40 shrink-0 w-6 h-6 flex items-center justify-center outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500/50"
            >
              <ResetGlyph size={13} />
            </button>
          </Tooltip>
          {(error || !parsed.valid) && (
            <span className="absolute left-0 top-full mt-0.5 text-[10px] text-red-400 whitespace-nowrap">
              {/* A save failure (`error`) wins over the range hint; the hint
                  only ever shows for a non-blank out-of-range value, since a
                  blank input is valid ("inherit"). Absolutely positioned, so
                  neither changes the field's footprint. */}
              {error || t('settings.versionRetention.invalid')}
            </span>
          )}
        </>
      ) : (
        <>
          <span
            data-testid={`vr-value-${id}`}
            // NOT `themed-input` when the value is explicit. This is a read-only
            // display behind a pencil, and `themed-input`'s fill made it look
            // like an editable box that happened to be disabled — the one thing
            // this shape exists to avoid. An inherited row already reads as the
            // quieter of the two through its muted TEXT, which is the difference
            // that matters; the fill was saying it a second time in the wrong
            // vocabulary.
            className={`border rounded px-2 py-1 text-xs w-16 truncate text-right tabular-nums themed-border ${
              value == null ? 'themed-text-muted' : 'themed-text'
            }`}
          >
            {value ?? inheritedPlaceholder}
          </span>
          <Tooltip content={t('settings.versionRetention.field.edit')} always placement="top" className="inline-flex shrink-0">
            <button
              type="button"
              data-testid={`vr-edit-${id}`}
              onClick={onEdit}
              aria-label={`${t('settings.versionRetention.field.edit')} ${label}`}
              className="themed-text-muted hover:themed-text shrink-0 w-6 h-6 flex items-center justify-center outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500/50"
            >
              <PencilGlyph size={13} />
            </button>
          </Tooltip>
        </>
      )}
    </div>
  )
}
